import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Save } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function ScreeningRulesAdmin({ orgId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowedTypes, setAllowedTypes] = useState([]);
  
  // Journey Automation Defaults
  const [journeyAutomationEnabled, setJourneyAutomationEnabled] = useState(false);
  const [resumeMinScore, setResumeMinScore] = useState(60);
  const [assessmentMinScore, setAssessmentMinScore] = useState(60);

  // The available types we want to support for now
  const ASSESSMENT_TYPES = [
    { id: 'multi_module', label: 'Multi-Module Assessment', description: 'Only allow Multi-Module assessments in the screening library selection.' },
    { id: 'standard', label: 'Standard Assessment', description: 'Allow standard assessments.' }
  ];

  useEffect(() => {
    fetchConfig();
  }, [orgId]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/internal/org-config/${orgId}`);
      const rules = res.data?.config?.screeningRules?.allowedAssessmentTypes || [];
      setAllowedTypes(rules);
      
      const ja = res.data?.config?.screeningRules?.journeyAutomation || {};
      setJourneyAutomationEnabled(ja.enabled || false);
      setResumeMinScore(ja.resumeMinScore ?? 60);
      setAssessmentMinScore(ja.assessmentMinScore ?? 60);
    } catch (err) {
      toast.error('Failed to load screening rules');
    } finally {
      setLoading(false);
    }
  };

  const toggleType = (id) => {
    setAllowedTypes(prev => {
      if (prev.includes(id)) {
        return prev.filter(t => t !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`/api/internal/org-config/${orgId}`, {
        screeningRules: {
          allowedAssessmentTypes: allowedTypes,
          journeyAutomation: {
            enabled: journeyAutomationEnabled,
            resumeMinScore,
            assessmentMinScore
          }
        }
      });
      toast.success('Screening rules updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#007BFF] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Screening Rules</h3>
          <p className="text-xs text-gray-500 mt-1">
            Restrict which assessment types this client can select when creating a new screening.
            If none are selected, all assessments will be visible by default.
          </p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={handleSave}
          className="inline-flex items-center gap-2 rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0069d9] transition-colors disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Rules'}
        </button>
      </div>

      <div className="grid gap-3">
        {ASSESSMENT_TYPES.map((type) => {
          const isSelected = allowedTypes.includes(type.id);
          return (
            <label
              key={type.id}
              className={cn(
                'flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                isSelected ? 'border-[#007BFF] bg-[#007BFF]/5' : 'border-gray-200 bg-white hover:border-gray-300'
              )}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleType(type.id)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 block">{type.label}</span>
                <span className="text-xs text-gray-500 block mt-0.5">{type.description}</span>
              </div>
            </label>
          );
        })}
      </div>

      <div className="mt-8 border-t border-gray-200 pt-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Journey Automation Defaults</h3>
        <p className="text-xs text-gray-500 mb-4">
          When enabled, existing screenings for this organization will be backfilled to require these minimum scores, and new screenings will default to these values.
        </p>

        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Enforce Qualification Gating</h4>
              <p className="text-xs text-gray-600 mt-0.5">
                Automatically reject candidates who do not meet the minimum score criteria.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={journeyAutomationEnabled}
              onClick={() => setJourneyAutomationEnabled(!journeyAutomationEnabled)}
              className={cn(
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007BFF]',
                journeyAutomationEnabled ? 'bg-[#007BFF]' : 'bg-gray-200'
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition',
                  journeyAutomationEnabled ? 'translate-x-5' : 'translate-x-0'
                )}
              />
            </button>
          </div>

          {journeyAutomationEnabled && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-200/60 pt-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Minimum Resume Score (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={resumeMinScore}
                  onChange={(e) => setResumeMinScore(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Minimum Assessment Score (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={assessmentMinScore}
                  onChange={(e) => setAssessmentMinScore(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
